// src/components/aiTalentDrive/TalentPoolLive.tsx
// The drive's live talent pool: percentages computed from candidates' CVs
// and AI interviews, served by GET /hiring-circuit/metrics/ (babblebots-
// monolith companyapp/hiring_circuit_metrics.py). Two faces of one fetch:
//   "full"    the whole of /ai-talent-drive/hiring-companies above its sign-in
//             form: hero headcount, then three bands (who's in the pool,
//             what they're good at, one interview). Styles: styles/company-signup.css
//   "bubbles" four facts floating on the right of the /ai-talent-drive hero
// Mostly shares, no names other than tracks: the one count
// shown anywhere is totals.registrations — every paid row, not deduplicated
// by email, so an upgrade or an extra Max track each count (2026-10-29,
// "numbers should be real" / "total registered, not only unique") — as the
// header subtitle, the "full" page's experience block, and the 4th bubble
// here. A block whose data is null (small pool, not tagged yet) is not
// drawn at all, so the page never shows a half number.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDownIcon, ArrowRightIcon } from "@phosphor-icons/react";
import { MagneticButton, VelocityMarquee } from "./driveMotion";
import ReceiverField from "./ReceiverField";
import { TRACKS, splitTrack } from "./tracks";
import "../../styles/talent-pool-live.css";

const POLL_MS = 60_000;

type Share = [string, number];
type TrackShare = [string, string, number];
type SkillShare = [string, string, number, number];
type Metrics = {
  computed_at: string;
  // registered = unique people with a paid row; registrations = every paid
  // row (an upgrade or an extra Max track adds a row for the same person,
  // and each is a real sign-up). The website's "total candidates" headline
  // reads registrations, not registered — see hiring_circuit_metrics.py.
  totals: { registered: number; registrations: number; cvs: number; interviewed: number };
  experience: { n: number; buckets: Share[] } | null;
  education: {
    n: number; coverage: number; tiers: [number, number][];
    tier1: { any: number; engineering: number; mba: number };
  } | null;
  availability: { n: number; buckets: Share[] } | null;
  location: {
    n: number; top: Share[]; other: number;
    relocation_open: number | null; hybrid_or_remote: number | null;
  } | null;
  employers: {
    n: number; coverage: number; names: string[]; product: string[]; services: string[];
    product_company_share: number;
  } | null;
  tracks: { n: number; top: TrackShare[] } | null;
  skill_score: {
    threshold: number; min_interviews: number; top: SkillShare[];
    candidate_rating: { average: number | null; rated_count: number } | null;
  } | null;
  snapshot: {
    clip_url: string; poster_url: string | null; duration: number;
    track: string; track_name: string; band: string; summary: string;
    screening: { notice?: string; location?: string; salary?: string; relocation?: string };
    skill_score: number | null;
  } | null;
};
const BAND_LABELS: Record<string, string> = { "0-2": "0–2 yrs", "2-5": "2–5 yrs", any: "", student: "Student" };

const EXPERIENCE_LABELS: Record<string, string> = { "0-2": "0–2 yrs", "2-5": "2–5 yrs", "5+": "5+ yrs" };
const SHORT_TRACK: Record<string, string> = {
  "FDE-01": "FDE", "GAI-02": "AI Engineer", "FS-03": "Full Stack", "BE-04": "Backend",
  "FE-05": "Frontend", "PM-06": "AI PM", "GTM-07": "GTM", "INT-08": "Intern", "DE-09": "Data Eng",
};
// Per-track colours for the hero's bubbles.
const TRACK_COLOURS: Record<string, string> = {
  "FDE-01": "#60A5FA", "GAI-02": "#22D3EE", "FS-03": "#86D9A0", "BE-04": "#FBBF24",
  "FE-05": "#F472B6", "PM-06": "#C4B5FD", "GTM-07": "#A3E635", "INT-08": "#FB923C", "DE-09": "#2DD4BF",
};

const share = (list: Share[] | undefined, key: string) => list?.find(([k]) => k === key)?.[1] ?? 0;
// Shares are shown as the API sends them, whole percentages. (A round-to-
// tens pass was tried on 2026-10-26 and reverted: it read worse.) Bars use
// the same value so they match the numbers.
const tens = (v: number) => Math.round(v);

// Tier 1 and tier 2 added together: the headline the education card shows,
// since tier 1 alone reads as a small number. Each candidate is counted once
// in the API's tiers (their best college), so the two shares can be summed.
// Everyone past the entry bracket: 2–5 and 5+ together
const twoPlusYears = (buckets: Share[]) =>
  Math.min(100, buckets.filter(([k]) => k !== "0-2").reduce((sum, [, v]) => sum + v, 0));

// The backend still sends four buckets (0-2, 2-5, 5-8, 8+); the chart shows
// 5-8 and 8+ as one "5+" bar. Done here rather than on the backend so this
// stays a frontend-only change: summing two backend-rounded percentages can
// in principle land the three displayed bars a point off 100%, which is an
// acceptable trade for not needing a backend deploy for a display change.
const mergeExperience = (buckets: Share[]): Share[] => {
  const merged: Share[] = [];
  let fivePlus = 0, has5Plus = false;
  for (const [k, v] of buckets) {
    if (k === "5-8" || k === "8+") { fivePlus += v; has5Plus = true; continue; }
    merged.push([k, v]);
  }
  if (has5Plus) merged.push(["5+", Math.min(100, fivePlus)]);
  return merged;
};

const tierShare = (edu: NonNullable<Metrics["education"]>, tier: number) =>
  edu.tiers.find(([t]) => t === tier)?.[1] ?? 0;
const tier12 = (edu: NonNullable<Metrics["education"]>) =>
  Math.min(100, edu.tier1.any + tierShare(edu, 2));

const reduced = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const SIGNIN_ID = "signin";
const scrollToSignin = () =>
  document.getElementById(SIGNIN_ID)?.scrollIntoView({ behavior: reduced() ? "auto" : "smooth", block: "start" });
const jumpToSignin = (e: React.MouseEvent<HTMLAnchorElement>) => {
  if (!document.getElementById(SIGNIN_ID)) return;       // no form on this page: let the hash work
  e.preventDefault();
  scrollToSignin();
};
const agoText = (iso: string, now: number) => {
  const mins = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000));
  return mins < 1 ? "Updated just now" : `Updated ${mins} min ago`;
};

/* ---------- a number that counts to its value ---------- */

// `unit` sets the suffix in its own, smaller span (the big numerals of the
// companies page); the hero bubbles leave it off and render as before.
function Pct({ value, suffix = "%", locale = false, unit = false }: { value: number; suffix?: string; locale?: boolean; unit?: boolean }) {
  const target = tens(value);
  const [shown, setShown] = useState(reduced() ? target : 0);
  const from = useRef(reduced() ? target : 0);
  useEffect(() => {
    if (reduced()) { setShown(target); return; }
    const start = from.current;
    const t0 = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / 1100);
      const e = 1 - Math.pow(1 - p, 3);
      const v = start + (target - start) * e;
      from.current = v;                // a change mid-count continues from here
      setShown(v);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  const shownRounded = Math.round(shown);
  const text = locale ? shownRounded.toLocaleString("en-IN") : shownRounded;
  return <>{text}{unit && suffix ? <span className="cs-unit">{suffix}</span> : suffix}</>;
}

/* ---------- the fetch ---------- */

type Status = "loading" | "ready" | "error";

// LOCAL PREVIEW ONLY — never runs on a deployed build. Gated on hostname,
// same check api.ts uses for the API base, so it can never fire in prod.
// The local Django backend's own pool is usually small (a handful of real
// registrations, under the backend's MIN_DENOMINATOR for most metrics), so
// this stands in a full sample payload instead, to see every block and
// bubble while working on this page locally. Remove this block once it is
// no longer needed; it does nothing on babblebots.ai.
// Setting REACT_APP_HIRING_CIRCUIT_API (api.ts) switches it off, so
// `REACT_APP_HIRING_CIRCUIT_API=https://interview.babblebots.ai npm start`
// shows the live pool on localhost:3000 (the one port the API's CORS allows).
const LOCAL_PREVIEW: Metrics | null =
  typeof window !== "undefined" && window.location.hostname === "localhost" && !process.env.REACT_APP_HIRING_CIRCUIT_API
    ? {
        computed_at: new Date().toISOString(),
        totals: { registered: 1010, registrations: 1284, cvs: 912, interviewed: 438 },
        experience: { n: 912, buckets: [["0-2", 34], ["2-5", 41], ["5-8", 17], ["8+", 8]] },
        education: { n: 880, coverage: 96, tiers: [[1, 23], [2, 31], [3, 46]], tier1: { any: 23, engineering: 18, mba: 5 } },
        availability: { n: 438, buckets: [["immediate", 29], ["<=30", 33], ["31-60", 24], ["60+", 14]] },
        location: {
          n: 1105, other: 7, relocation_open: 46, hybrid_or_remote: 58,
          top: [["Bengaluru", 31], ["Hyderabad", 20], ["Pune", 13], ["Delhi NCR", 12], ["Mumbai", 9], ["Remote", 8]],
        },
        employers: {
          n: 870, coverage: 95, product_company_share: 38,
          names: ["Accenture", "Acme AI", "Amazon", "Capgemini", "Cognizant", "CRED", "Deloitte", "Flipkart",
                  "Freshworks", "HCLTech", "HDFC Bank", "Infosys", "Meesho", "Oracle", "PhonePe", "Razorpay",
                  "Swiggy", "TCS", "Wipro", "Zoho"],
          product: ["Amazon", "CRED", "Flipkart", "Freshworks", "Meesho", "Oracle", "PhonePe", "Razorpay", "Swiggy", "Zoho"],
          services: ["Accenture", "Capgemini", "Cognizant", "Deloitte", "HCLTech", "Infosys", "TCS", "Wipro"],
        },
        tracks: {
          n: 1500,
          top: [["GAI-02", "AI Engineer", 27], ["FS-03", "Full Stack Engineer", 22], ["BE-04", "Backend Engineer", 16],
                ["INT-08", "Software / AI Engineering Intern", 13], ["FE-05", "Frontend Engineer", 9]],
        },
        skill_score: {
          threshold: 7, min_interviews: 10,
          top: [["BE-04", "Backend Engineer", 46, 7.4], ["GAI-02", "AI Engineer", 41, 7.1], ["PM-06", "AI Product Manager", 38, 7.0],
                ["FS-03", "Full Stack Engineer", 35, 6.8], ["FE-05", "Frontend Engineer", 31, 6.6]],
          candidate_rating: { average: 4.6, rated_count: 311 },
        },
        snapshot: {
          clip_url: "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4", poster_url: null, duration: 15,
          track: "BE-04", track_name: "Backend Engineer", band: "2-5",
          summary: "Owns backend services end to end and reasons clearly about consistency versus latency. Strong on relational data and queues, lighter on container orchestration. Communicates precisely.",
          screening: { notice: "Within 1 month", location: "Bengaluru · Hybrid", salary: "₹18 LPA", relocation: "Open" },
          skill_score: 8.5,
        },
      }
    : null;

function useMetrics(apiBase: string) {
  const [data, setData] = useState<Metrics | null>(LOCAL_PREVIEW);
  // "error" only while nothing has ever loaded: a failed refresh keeps the
  // figures on screen and tries again at the next poll.
  const [status, setStatus] = useState<Status>(LOCAL_PREVIEW ? "ready" : "loading");
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (LOCAL_PREVIEW) return;
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(`${apiBase}/hiring-circuit/metrics/`);
        if (!alive) return;
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as Metrics;
        if (!alive) return;
        if (body && body.totals) {
          setData(body);
          setStatus("ready");
        } else {
          throw new Error("bad payload");
        }
      } catch {
        if (alive) setStatus((s) => (s === "ready" ? s : "error"));
      }
    };
    load();
    const tick = () => { if (document.visibilityState === "visible") load(); };
    const poll = window.setInterval(tick, POLL_MS);
    const clock = window.setInterval(() => setNow(Date.now()), 30_000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      alive = false;
      window.clearInterval(poll);
      window.clearInterval(clock);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [apiBase]);
  return { data, status, now };
}

/* ---------- pieces ---------- */

type Part = { label: string; value: number };

// label · bar · value, one per row
const Parts = ({ parts }: { parts: Part[] }) => (
  <ul className="cs-parts">
    {parts.map((p) => (
      <li key={p.label}>
        <span>{p.label}</span>
        <i className="cs-bar"><i style={{ width: `${Math.min(100, tens(p.value))}%` }} /></i>
        <b><Pct value={p.value} /></b>
      </li>
    ))}
  </ul>
);

// One 100% bar cut into segments, with a legend under it. tone 1 is the
// strongest segment, 4 the faintest; tone 0 is the dimmed "everyone else".
type Seg = { label: string; value: number; tone: 0 | 1 | 2 | 3 | 4 };
const Segments = ({ segs, label }: { segs: Seg[]; label: string }) => (
  <div className="cs-seg">
    <div className="cs-stack" role="img" aria-label={label}>
      {segs.map((s) => (
        <i key={s.label} className={`cs-stack__seg cs-stack__seg--${s.tone}`} style={{ flexGrow: Math.max(1, tens(s.value)) }} />
      ))}
    </div>
    <ul className="cs-legend">
      {segs.map((s) => (
        <li key={s.label}><i className={`cs-stack__seg cs-stack__seg--${s.tone}`} />{s.label}<b><Pct value={s.value} /></b></li>
      ))}
    </ul>
  </div>
);

type RankRow = { code: string; name: string; value: number };
const RankList = ({ title, rows }: { title: string; rows: RankRow[] }) => (
  <article className="cs-rank hc-fade">
    <h3>{title}</h3>
    <ol>
      {rows.map((r, i) => (
        <li key={r.code} className={i === 0 ? "is-top" : undefined}>
          <span className="cs-rank__n">{String(i + 1).padStart(2, "0")}</span>
          <span className="cs-rank__nm"><small>{r.code}</small>{r.name}</span>
          <b className="cs-rank__v"><Pct value={r.value} unit /></b>
          <i className="cs-bar"><i style={{ width: `${tens(r.value)}%` }} /></i>
        </li>
      ))}
    </ol>
  </article>
);

/* ---------- reveal on scroll ---------- */

// The page-level useRevealOnScroll only sees what exists at mount; these
// blocks appear when the data lands, so they get their own observer, run
// again whenever the data changes.
function useRevealWhenLoaded(ref: React.RefObject<HTMLElement | null>, deps: unknown[]) {
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const targets = root.querySelectorAll(".hc-fade:not(.visible)");
    if (!targets.length) return;
    if (!("IntersectionObserver" in window)) {
      targets.forEach((el) => el.classList.add("visible"));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/* ---------- full: the companies page ---------- */

const TRACK_NAMES = TRACKS.map((t) => splitTrack(t).name);
const PERKS = "Free for hiring teams · Watch candidate video interviews · Download CVs";
const NOTICE_LABELS: [string, string][] = [["immediate", "Immediate"], ["<=30", "≤ 30 days"], ["31-60", "31–60 days"], ["60+", "60+ days"]];
// years ascending, so the two right-hand segments are the headline "2+ years"
const EXPERIENCE_TONES: [string, Seg["tone"]][] = [["0-2", 0], ["2-5", 2], ["5+", 1]];

const SectionHead = ({ n, tag, children, sub }: { n: string; tag: string; children: React.ReactNode; sub?: string }) => (
  <div className="td-head hc-fade">
    <span className="section-tag">{n} · {tag}</span>
    <h2>{children}</h2>
    {sub && <p>{sub}</p>}
  </div>
);

function Full({ data, status, now }: { data: Metrics | null; status: Status; now: number }) {
  const rootRef = useRef<HTMLDivElement>(null);
  // Stable callback: React would re-run an inline ref on every render and
  // re-mute the clip under a viewer who turned the sound on. Browsers only
  // autoplay muted video, and React does not reliably write `muted`.
  const muteOnMount = useCallback((el: HTMLVideoElement | null) => {
    if (el) { el.muted = true; el.defaultMuted = true; }
  }, []);
  useRevealWhenLoaded(rootRef, [data]);
  const exp = data?.experience ?? null;
  const edu = data?.education ?? null;
  const avail = data?.availability ?? null;
  const loc = data?.location ?? null;
  const tracks = data?.tracks ?? null;
  const skill = data?.skill_score ?? null;
  const snap = data?.snapshot ?? null;
  // The headcount (user, 2026-10-29): every paid registration, not just
  // unique people. See the Metrics type above.
  const total = data?.totals.registrations ?? 0;

  const expSegs = useMemo<Seg[]>(() => {
    if (!exp) return [];
    const merged = mergeExperience(exp.buckets);
    return EXPERIENCE_TONES.flatMap(([k, tone]) => {
      const hit = merged.find(([key]) => key === k);
      return hit ? [{ label: EXPERIENCE_LABELS[k] ?? k, value: hit[1], tone }] : [];
    });
  }, [exp]);

  const move = loc && loc.relocation_open !== null ? loc : null;
  const tiles = [expSegs.length > 0, !!edu, !!avail, !!move].filter(Boolean).length;
  const anyBlock = tiles > 0 || loc || tracks || skill || snap;
  const cities: Share[] = loc ? [...loc.top, ...(loc.other > 0 ? [["Other", loc.other] as Share] : [])] : [];
  const citiesMax = Math.max(1, ...cities.map((c) => tens(c[1])));

  return (
    <div className="tp tp--full" ref={rootRef}>
      {/* ---------- Hero: the headcount, received ---------- */}
      <section className="cs-hero" aria-labelledby="cs-title">
        <ReceiverField />
        <div className="wrap cs-hero__inner">
          <p className="cs-hero__kicker">
            <span className="tp-dot" aria-hidden="true" />
            <span>
              Live talent pool
              {data && <><span aria-hidden="true"> · </span>{agoText(data.computed_at, now)}</>}
            </span>
          </p>
          <h1 className="cs-hero__title" id="cs-title">
            <span className="td-sr">Hire from the AI Talent Drive: </span>
            <span className="cs-line"><span>Little peek,</span></span>
            <span className="cs-line"><span>at a lot of <em>talent…</em></span></span>
          </h1>

          <div className="cs-hero__count">
            {total > 0 ? (
              <>
                <b className="cs-count" data-receiver-anchor>
                  <span className="cs-count__ghost" aria-hidden="true">{total.toLocaleString("en-IN")}</span>
                  <span><Pct value={total} suffix="" locale /></span>
                </b>
                <p className="cs-count__label"><strong>candidates</strong> and counting</p>
              </>
            ) : status === "loading" ? (
              <b className="cs-count cs-count--wait" data-receiver-anchor aria-hidden="true">0,000</b>
            ) : null}
          </div>

          <div className="cs-hero__cta">
            <MagneticButton className="td-btn td-btn--lime" onClick={scrollToSignin}>
              <span>Get free access</span>
              <ArrowDownIcon size={20} weight="bold" aria-hidden="true" />
            </MagneticButton>
            <p>{PERKS}</p>
          </div>
        </div>
      </section>

      <VelocityMarquee items={TRACK_NAMES} />

      {/* ---------- 01 Who is in the pool: four numbers, then where ---------- */}
      <section className="td-band td-band--paper cs-pool" aria-labelledby="cs-pool-title">
        <div className="wrap">
          <SectionHead n="01" tag="The pool" sub="Percentages computed from candidates’ CVs and AI interviews.">
            <span id="cs-pool-title">Who’s in the Talent <em>pool?</em></span>
          </SectionHead>

          {status === "loading" && (
            <div className="cs-skeleton" role="status" aria-live="polite" aria-label="Loading the talent pool"><span /><span /><span /><span /></div>
          )}
          {status === "error" && (
            <p className="cs-empty" role="status">
              Live figures are unavailable right now. We’ll try again in a minute; the sign-in form below still works.
            </p>
          )}
          {status === "ready" && !anyBlock && (
            <p className="cs-empty" role="status">
              The first candidates are registering. Figures appear once the pool is large enough to talk about in percentages.
            </p>
          )}

          <div className="cs-bento">
            {tiles > 0 && (
              <div className="cs-tiles" style={{ "--n": tiles } as React.CSSProperties}>
                {expSegs.length > 0 && exp && (
                  <article className="cs-card cs-card--ink hc-fade">
                    <h3>Years of experience</h3>
                    <div>
                      {/* Everyone past the entry bracket: 2–5 and 5+ together */}
                      <b className="cs-big"><Pct value={twoPlusYears(exp.buckets)} unit /></b>
                      <p className="cs-card__line">have <strong>2+ years</strong> of experience</p>
                    </div>
                    <Segments segs={expSegs} label="Experience split" />
                  </article>
                )}

                {edu && (
                  <article className="cs-card cs-card--indigo hc-fade">
                    <h3>Tier-1/2 education</h3>
                    <div>
                      <b className="cs-big"><Pct value={tier12(edu)} unit /></b>
                      <p className="cs-card__line">IIT · NIT · IIIT · BITS · IIM · ISB, top state and private institutes</p>
                    </div>
                    {/* The API splits engineering/MBA for tier 1 only; the
                        tier 1/2 split needs the backend, so these are tier-1 figures. */}
                    <Parts parts={[{ label: "Engineering", value: edu.tier1.engineering }, { label: "MBA", value: edu.tier1.mba }]} />
                  </article>
                )}

                {avail && (
                  <article className="cs-card cs-card--lime hc-fade">
                    <h3>Immediate joiners</h3>
                    <div>
                      <b className="cs-big"><Pct value={share(avail.buckets, "immediate")} unit /></b>
                      <p className="cs-card__line">Notice period, captured in the AI interview</p>
                    </div>
                    <Segments
                      label="Notice period split"
                      segs={NOTICE_LABELS.map(([k, label], i) => ({ label, value: share(avail.buckets, k), tone: (i + 1) as Seg["tone"] }))}
                    />
                  </article>
                )}

                {move && (
                  <article className="cs-card cs-card--paper hc-fade">
                    <h3>Open to relocation</h3>
                    <div>
                      <b className="cs-big"><Pct value={move.relocation_open ?? 0} unit /></b>
                      <p className="cs-card__line">Will move for the right role</p>
                    </div>
                    {move.hybrid_or_remote !== null && (
                      <Segments
                        label="Hybrid or remote versus onsite"
                        segs={[
                          { label: "Hybrid or remote", value: move.hybrid_or_remote, tone: 1 },
                          { label: "Onsite is fine", value: 100 - move.hybrid_or_remote, tone: 3 },
                        ]}
                      />
                    )}
                  </article>
                )}
              </div>
            )}

            {loc && (
              <article className="cs-card cs-card--ink cs-card--loc hc-fade">
                <h3>Preferred location <small>First preference per candidate</small></h3>
                <ol className="cs-cities" style={{ "--rows": Math.ceil(cities.length / 2) } as React.CSSProperties}>
                  {cities.map(([city, v], i) => (
                    <li key={city} className={i === 0 ? "is-top" : undefined}>
                      <span className="cs-cities__n">{String(i + 1).padStart(2, "0")}</span>
                      <span className="cs-cities__nm">{city}</span>
                      <i className="cs-bar"><i style={{ width: `${(tens(v) / citiesMax) * 100}%` }} /></i>
                      <b><Pct value={v} /></b>
                    </li>
                  ))}
                </ol>
              </article>
            )}
          </div>
        </div>
      </section>

      {/* ---------- 02 What they are good at ---------- */}
      {(tracks || skill) && (
        <section className="td-band td-band--indigo cs-skills" aria-labelledby="cs-skills-title">
          <div className="wrap">
            <SectionHead n="02" tag="Nine role tracks">
              <span id="cs-skills-title">What are they <em>good at?</em></span>
            </SectionHead>
            <div className="cs-skills__grid">
              {tracks && <RankList title="Hottest tracks" rows={tracks.top.map(([code, name, value]) => ({ code, name, value }))} />}
              {skill && <RankList title="Strongest skill scores" rows={skill.top.map(([code, name, value]) => ({ code, name, value }))} />}
            </div>
          </div>
        </section>
      )}

      {/* ---------- 03 One candidate, as a recruiter sees them ---------- */}
      {snap && (
        <section className="td-band cs-meet" aria-labelledby="cs-meet-title">
          <div className="wrap cs-meet__grid">
            <div className="cs-meet__stage hc-fade">
              <div className="cs-meet__video">
                {/* Starts silent; the speaker control turns the voice on */}
                <video
                  src={snap.clip_url} poster={snap.poster_url ?? undefined}
                  autoPlay loop playsInline controls preload="metadata"
                  ref={muteOnMount}
                />
                <span className="cs-meet__badge"><i className="tp-dot" aria-hidden="true" /> AI interview · {snap.duration}s</span>
              </div>
            </div>
            <div className="cs-meet__body hc-fade">
              <span className="section-tag">03 · Meet a candidate</span>
              <h2 id="cs-meet-title">{snap.track_name || "Drive candidate"}</h2>
              {BAND_LABELS[snap.band] && <p className="cs-meet__sub">{BAND_LABELS[snap.band]} experience · recently interviewed</p>}
              {(snap.screening?.notice || snap.screening?.location || snap.screening?.relocation) && (
                <dl className="cs-meet__facts">
                  {snap.screening.notice && <div><dt>Notice</dt><dd>{snap.screening.notice}</dd></div>}
                  {snap.screening.location && <div><dt>Location</dt><dd>{snap.screening.location}</dd></div>}
                  {snap.screening.relocation && <div><dt>Relocation</dt><dd>{snap.screening.relocation}</dd></div>}
                </dl>
              )}
              <a className="td-btn td-btn--lime" href={`#${SIGNIN_ID}`} onClick={jumpToSignin}>
                <span>Sign-up to see full video interviews</span>
                <ArrowRightIcon size={20} weight="bold" aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/* ---------- bubbles: the hero ---------- */

// Slots are filled in order by whichever facts exist, so a missing metric
// never leaves a hole in the composition.
const BIG = [
  { left: "30%", top: "40%", d: 188, fs: 40, tone: "lime", period: "7s", delay: "0s" },
  { left: "66%", top: "26%", d: 150, fs: 32, tone: "cyan", period: "8s", delay: "-3s" },
  { left: "22%", top: "80%", d: 136, fs: 30, tone: "amber", period: "9s", delay: "-5s" },
  // The headcount (totals.registrations): same size as the amber bubble, in
  // the open top-left corner. Checked by actual centre-to-centre distance
  // against bubble 1 (not just a box overlap, since both are circles):
  // ~193px apart at this d, against a 94+68=162px touching distance, so
  // there is ~30px of clearance even at the float animation's extremes.
  { left: "8%", top: "7%", d: 136, fs: 30, tone: "violet", period: "7.6s", delay: "-1.5s" },
];
const SMALL = [
  { left: "72%", top: "60%", d: 96, fs: 18, period: "6s", delay: "-1s" },
  { left: "50%", top: "76%", d: 84, fs: 16, period: "7s", delay: "-4s" },
  { left: "90%", top: "48%", d: 72, fs: 15, period: "8s", delay: "-2s" },
  { left: "88%", top: "80%", d: 66, fs: 14, period: "6.5s", delay: "-6s" },
  { left: "66%", top: "88%", d: 58, fs: 13, period: "7.5s", delay: "-2.5s" },
];

// One glass sphere: the number, its label, and (for the big ones) a thin
// glowing arc around the rim, drawn to `ring`% of the circle.
const Bubble = ({ value, label, className, style, arc, colour, suffix, locale, ring }: {
  value: number; label: string; className: string; style: React.CSSProperties; arc?: boolean; colour?: string;
  // For the one bubble that is a headcount, not a share: suffix="" and
  // locale=true print "1,284" instead of "1284%".
  suffix?: string; locale?: boolean;
  // The arc's own fill, 0-100: defaults to the displayed value (a real
  // share reads as that much of the ring). The headcount bubble passes 100
  // instead — a full, solid glow, since it isn't a share of anything — so
  // it keeps the same lit-up rim look as the others rather than reading as
  // a flat, colourless circle.
  ring?: number;
}) => {
  const [entered, setEntered] = useState(false);
  useEffect(() => { const id = requestAnimationFrame(() => setEntered(true)); return () => cancelAnimationFrame(id); }, []);
  const ringPct = ring ?? tens(value);
  return (
    <div className={`tp-bub ${className}${entered ? " tp-bub--in" : ""}`} style={{ ...style, ...(colour ? { "--c": colour } : {}) } as React.CSSProperties}>
      {arc && (
        <svg className="tp-bub-arc" viewBox="0 0 100 100" aria-hidden="true">
          <circle cx="50" cy="50" r="47" pathLength={100} fill="none" stroke="rgba(255,255,255,.07)" strokeWidth="1.2" />
          <circle cx="50" cy="50" r="47" pathLength={100} fill="none" strokeWidth="1.8" strokeLinecap="round"
            strokeDasharray={`${ringPct} ${100 - ringPct}`} strokeDashoffset="25" />
        </svg>
      )}
      <span className="tp-bub-shine" aria-hidden="true" />
      <b><Pct value={value} suffix={suffix} locale={locale} /></b>
      <span>{label}</span>
    </div>
  );
};

type Fact = { key: string; value: number; label: string; suffix?: string; locale?: boolean; arc?: boolean };

function Bubbles({ data, now }: { data: Metrics | null; now: number }) {
  // These three compact into BIG[0..2] exactly as before: a missing one
  // (small pool, not enough for that metric yet) never leaves a gap.
  const mainFacts = [
    data?.experience ? { key: "exp", value: twoPlusYears(data.experience.buckets), label: "have 2+ yrs experience" } : null,
    data?.availability ? { key: "avail", value: share(data.availability.buckets, "immediate"), label: "can join immediately" } : null,
    data?.education ? { key: "edu", value: tier12(data.education), label: "tier-1/2 education" } : null,
  ].filter(Boolean) as Fact[];
  // The headcount is its own, always-BIG[3] bubble: it has no MIN_DENOMINATOR
  // gate on the backend (totals.registrations is set as soon as anyone is
  // registered), so it must never slide into a slot left open by one of the
  // three above being null — that would silently swap out "2+ yrs
  // experience" for the headcount instead of adding to the cluster.
  const totalFact: Fact | null = data?.totals.registrations
    ? { key: "total", value: data.totals.registrations, label: "total candidates", suffix: "", locale: true }
    : null;
  const tracks = data?.tracks?.top ?? [];
  if (!data || (!mainFacts.length && !totalFact && !tracks.length)) return null;

  return (
    <div className="tp tp--bubbles" aria-label="Live talent pool">
      {mainFacts.map((f, i) => {
        const slot = BIG[i];
        if (!slot) return null;
        return (
          <Bubble key={f.key} value={f.value} label={f.label} arc={f.arc !== false} suffix={f.suffix} locale={f.locale}
            className={`tp-bub--big tp-bub--${slot.tone}`}
            style={{ "--d": `${slot.d}px`, "--fs": `${slot.fs}px`, "--t": slot.period, "--delay": slot.delay, left: slot.left, top: slot.top } as React.CSSProperties} />
        );
      })}
      {totalFact && (
        <Bubble key={totalFact.key} value={totalFact.value} label={totalFact.label} arc ring={100}
          suffix={totalFact.suffix} locale={totalFact.locale}
          className={`tp-bub--big tp-bub--${BIG[3].tone}`}
          style={{ "--d": `${BIG[3].d}px`, "--fs": `${BIG[3].fs}px`, "--t": BIG[3].period, "--delay": BIG[3].delay, left: BIG[3].left, top: BIG[3].top } as React.CSSProperties} />
      )}
      {/* The live badge sits with the tracks label, under the cluster */}
      {tracks.length > 0 && <span className="tp-chip tp-lbl-tracks">Hottest tracks</span>}
      <div className="tp-cloud-foot">
        <span className="tp-chip tp-chip--live">
          <span className="tp-dot" /> Live
          <i aria-hidden="true" />
          <span className="tp-chip-sub">{agoText(data.computed_at, now)}</span>
        </span>
      </div>
      {tracks.slice(0, SMALL.length).map(([code, name, v], i) => (
        <Bubble key={code} value={v} label={SHORT_TRACK[code] ?? name} colour={TRACK_COLOURS[code] ?? "#22D3EE"}
          className={`tp-bub--track${i === 0 ? " tp-bub--hot" : ""}`}
          style={{ "--d": `${SMALL[i].d}px`, "--fs": `${SMALL[i].fs}px`, "--t": SMALL[i].period, "--delay": SMALL[i].delay, left: SMALL[i].left, top: SMALL[i].top } as React.CSSProperties} />
      ))}
    </div>
  );
}

/* ---------- export ---------- */

type Props = { apiBase: string; variant: "full" | "bubbles" };

export default function TalentPoolLive({ apiBase, variant }: Props) {
  const { data, status, now } = useMetrics(apiBase);
  // The hero has nothing to say while loading or on failure: the wave
  // simply keeps the right half, as it did before the pool existed.
  return variant === "full" ? <Full data={data} status={status} now={now} /> : <Bubbles data={data} now={now} />;
}
